export type PartnerRouteContext = {
	reservedPartnerRouteSegments: readonly string[];
};

export type CanonicalRouteResult =
	| { kind: "redirect"; href: string }
	| { kind: "unresolvable"; segment: string }
	| null;

type RouteNodeLike = {
	readonly fullPath?: string;
	readonly children?: unknown;
	readonly options?: unknown;
};

export function deriveReservedPartnerRouteSegmentsFromRouteTree(routeTree: RouteNodeLike): readonly string[] {
	const reservedSegments = new Set<string>();
	const visited = new Set<RouteNodeLike>();

	const visit = (route: RouteNodeLike) => {
		if (visited.has(route)) return;
		visited.add(route);

		if (typeof route.fullPath === "string") {
			const segments = route.fullPath.split("/").filter(Boolean);
			const partnerIndex = segments.findIndex((s, i) => s === "$partner" && segments[i - 1] === "$locale");
			const reserved = partnerIndex >= 0 ? segments[partnerIndex + 1] : undefined;
			if (reserved && !reserved.startsWith("$")) reservedSegments.add(reserved);
		}

		for (const child of getRouteChildren(route)) visit(child);
	};

	visit(routeTree);
	return [...reservedSegments];
}

function getRouteChildren(route: RouteNodeLike): readonly RouteNodeLike[] {
	const optionsChildren =
		typeof route.options === "object" && route.options !== null
			? (route.options as { children?: unknown }).children
			: undefined;
	const children = route.children ?? optionsChildren;
	if (!children || typeof children !== "object") return [];
	return Object.values(children) as RouteNodeLike[];
}

function isReservedSegment(segment: string | undefined, reservedSegments: ReadonlySet<string>): segment is string {
	return Boolean(segment && reservedSegments.has(segment));
}

type ResolvePartnerRouteCandidateInput = {
	resolvedPartner?: string | null;
	currentPartnerSegment: string;
	reservedPartnerSegments: Iterable<string>;
	storedPartner?: string | null;
};

function resolvePartnerRouteCandidate({
	resolvedPartner,
	currentPartnerSegment,
	reservedPartnerSegments,
	storedPartner,
}: ResolvePartnerRouteCandidateInput): string | null {
	if (resolvedPartner) return resolvedPartner;

	const reservedSegments = new Set(reservedPartnerSegments);
	if (!isReservedSegment(currentPartnerSegment, reservedSegments)) return currentPartnerSegment;

	return storedPartner ?? null;
}

type ResolveCanonicalPartnerInput = {
	search: Record<string, unknown>;
	currentPartnerSegment: string;
	reservedPartnerSegments: Iterable<string>;
	resolvePartner: (
		search: Record<string, unknown>,
		currentPartnerSegment: string,
	) => string | null | undefined | Promise<string | null | undefined>;
	getStoredPartner?: () => string | null | undefined;
};

async function resolveCanonicalPartner({
	search,
	currentPartnerSegment,
	reservedPartnerSegments,
	resolvePartner,
	getStoredPartner,
}: ResolveCanonicalPartnerInput): Promise<string | null> {
	let resolvedPartner: string | null | undefined;
	try {
		resolvedPartner = await resolvePartner(search, currentPartnerSegment);
	} catch {
		// fall through to URL / stored-partner resolution
	}

	return resolvePartnerRouteCandidate({
		resolvedPartner,
		currentPartnerSegment,
		reservedPartnerSegments,
		storedPartner: getStoredPartner?.(),
	});
}

export type CanonicalizePartnerRouteInput = Omit<CanonicalizePartnerSegmentInput, "resolvePartner"> & {
	resolvePartner: CanonicalizePartnerSegmentInput["resolvePartner"];
	getStoredPartner?: () => string | null | undefined;
};

export function canonicalizePartnerRoute(input: CanonicalizePartnerRouteInput): Promise<CanonicalRouteResult> {
	return canonicalizePartnerSegment({
		...input,
		resolvePartner: (search, currentPartnerSegment) =>
			resolveCanonicalPartner({
				search,
				currentPartnerSegment,
				reservedPartnerSegments: input.reservedPartnerSegments,
				resolvePartner: input.resolvePartner,
				getStoredPartner: input.getStoredPartner,
			}),
	});
}

function buildHref(pathname: string, search: Record<string, unknown>): string {
	const params = new URLSearchParams();
	for (const [key, value] of Object.entries(search)) {
		if (value === undefined || value === null || value === "") continue;
		if (Array.isArray(value)) {
			for (const item of value) {
				if (item !== undefined && item !== null && item !== "") params.append(key, String(item));
			}
			continue;
		}
		params.set(key, String(value));
	}
	const qs = params.toString();
	return qs ? `${pathname}?${qs}` : pathname;
}

async function resolveAndValidatePartner(
	callback: () => string | null | undefined | Promise<string | null | undefined>,
	reservedSegments: ReadonlySet<string>,
): Promise<string | null> {
	const resolved = (await callback()) ?? null;
	return isReservedSegment(resolved ?? undefined, reservedSegments) ? null : resolved;
}

export type CanonicalizeRootLocaleUrlInput = {
	pathname: string;
	search: Record<string, unknown>;
	isLocale: (segment: string) => boolean;
	resolveLocale: (legacyLocale: string) => string;
	reservedPartnerSegments: Iterable<string>;
	resolvePartner: (search: Record<string, unknown>) => string | null | undefined | Promise<string | null | undefined>;
};

export async function canonicalizeRootLocaleUrl(input: CanonicalizeRootLocaleUrlInput): Promise<CanonicalRouteResult> {
	const { locale: _locale, partnerCode: _partnerCode, ...cleanedSearch } = input.search;
	const pathSegments = input.pathname.split("/").filter(Boolean);
	const [firstSegment] = pathSegments;
	const hasLocaleInPath = Boolean(firstSegment && input.isLocale(firstSegment));
	const partnerFromSearch = typeof input.search.partnerCode === "string" ? input.search.partnerCode : null;

	// `/locale?partnerCode=foo` → `/locale/foo` (partner segment missing from path, supplied via query).
	if (partnerFromSearch && firstSegment && hasLocaleInPath && pathSegments.length === 1) {
		const canonicalLocale = input.resolveLocale(firstSegment);
		return { kind: "redirect", href: buildHref(`/${canonicalLocale}/${partnerFromSearch}`, cleanedSearch) };
	}

	const legacyLocale = typeof input.search.locale === "string" ? input.search.locale : null;
	if (!legacyLocale) return null;

	if (hasLocaleInPath) {
		return { kind: "redirect", href: buildHref(input.pathname, cleanedSearch) };
	}

	const locale = input.resolveLocale(legacyLocale);
	const reservedSegments = new Set(input.reservedPartnerSegments);

	if (!isReservedSegment(firstSegment, reservedSegments)) {
		return { kind: "redirect", href: buildHref(`/${locale}${input.pathname}`, cleanedSearch) };
	}

	const partner = await resolveAndValidatePartner(() => input.resolvePartner(input.search), reservedSegments);
	if (!partner) return { kind: "unresolvable", segment: firstSegment };

	return {
		kind: "redirect",
		href: buildHref(`/${locale}/${partner}${input.pathname}`, cleanedSearch),
	};
}

export type CanonicalizePartnerSegmentInput = {
	pathname: string;
	search: Record<string, unknown>;
	locale: string;
	currentPartnerSegment: string;
	reservedPartnerSegments: Iterable<string>;
	resolvePartner: (
		search: Record<string, unknown>,
		currentPartnerSegment: string,
	) => string | null | undefined | Promise<string | null | undefined>;
};

export async function canonicalizePartnerSegment(
	input: CanonicalizePartnerSegmentInput,
): Promise<CanonicalRouteResult> {
	const reservedSegments = new Set(input.reservedPartnerSegments);
	const current = input.currentPartnerSegment;
	const currentPrefix = `/${input.locale}/${current}`;
	const { partnerCode: _partnerCode, ...cleanedSearch } = input.search;

	if (isReservedSegment(current, reservedSegments)) {
		const partner = await resolveAndValidatePartner(
			() => input.resolvePartner(input.search, current),
			reservedSegments,
		);
		if (!partner) return { kind: "unresolvable", segment: current };

		return {
			kind: "redirect",
			href: buildHref(input.pathname.replace(currentPrefix, `/${input.locale}/${partner}/${current}`), cleanedSearch),
		};
	}

	const partner = await resolveAndValidatePartner(() => input.resolvePartner(input.search, current), reservedSegments);
	if (!partner || partner === current) return null;

	return {
		kind: "redirect",
		href: buildHref(input.pathname.replace(currentPrefix, `/${input.locale}/${partner}`), cleanedSearch),
	};
}
